/**
 * Generate evenly spaced "nice" ticks for chart axes.
 *
 * - Ensures ticks are aligned to "nice" intervals (1, 2, 5 × 10^n).
 * - The maximum tick will be strictly greater than `max` unless `max` is already
 *   a multiple of the step (in which case no padding is added).
 * - The total number of ticks will not exceed `maxTicks`.
 * - Handles edge cases where `min === max` or `domainScale` is undefined.
 *
 * @param domainScale - A tuple `[min, max]` defining the chart value range.
 * @param maxTicks - Maximum number of ticks to generate (default: 5).
 * @returns An array of tick values aligned to a "nice" step size.
 *
 * @example
 * generateTicks([0, 11000]);
 * // → [0, 5000, 10000, 15000]
 *
 * generateTicks([0, 10000]);
 * // → [0, 5000, 10000]
 *
 * generateTicks([-1234, 567]);
 * // → [-2000, -1000, 0, 1000]
 *
 * generateTicks([0, 4]);
 * // → [0, 2, 4]
 */
export function generateTicks(domainScale: [number, number] | undefined, maxTicks = 5): number[] {
  if (!domainScale) return [];

  const [min, max] = domainScale;
  if (min === max) return [min]; // edge case

  const range = max - min;
  const roughStep = range / (maxTicks - 1);

  // Find a "nice" step (1, 2, 5 × 10^n)
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const multiples = [1, 2, 5, 10];
  let step = multiples.find((m) => roughStep <= m * magnitude)! * magnitude;

  // Calculate nice min & max
  let niceMin = Math.floor(min / step) * step;
  let niceMax = Math.ceil(max / step) * step;

  // Only add padding if max is NOT already a multiple of step
  if (niceMax <= max && max % step !== 0) {
    niceMax += step;
  }

  // If tick count exceeds maxTicks, increase step until it fits
  while ((niceMax - niceMin) / step + 1 > maxTicks) {
    step *= 2;
    niceMin = Math.floor(min / step) * step;
    niceMax = Math.ceil(max / step) * step;
    if (niceMax <= max && max % step !== 0) {
      niceMax += step;
    }
  }

  const ticks: number[] = [];
  for (let t = niceMin; t <= niceMax; t += step) {
    ticks.push(t);
  }

  return ticks;
}
